"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, MessageSquare, ShieldCheck, Sparkles, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const keyBenefits = [{
  title: "Method Feasibility & Assessment",
  description: "Detailed sample evaluation and technical feasibility testing before instrument selection."
}, {
  title: "Regulatory Compliance Support",
  description: "Full qualification packages (IQ/OQ/PQ) tailored for regulated GMP and FDA standards."
}, {
  title: "Lifecycle & Local Support",
  description: "Hands-on engineering, calibration, and training across Ireland and the UK."
}];
export default function DiscussCta() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="discussapplicationcta" data-nav="light" className="bg-background py-16 sm:py-24 border-t border-border overflow-hidden">
      <div className="page-container">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="relative bg-secondary text-secondary-foreground rounded-2xl border border-border p-8 sm:p-12 lg:p-16 shadow-lg overflow-hidden">
          {/* Subtle decorative background glow */}
          <div className="absolute -right-24 -bottom-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -left-24 -top-24 w-80 h-80 bg-accent/30 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left Column: Heading & Call to Action */}
            <motion.div variants={itemVariants} className="lg:col-span-7 flex flex-col gap-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-background border border-border text-foreground text-xs font-semibold uppercase tracking-wider self-start shadow-xs">
                <Sparkles className="size-3.5 text-primary" />
                <span>Application-Led Expertise</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
                Ready to optimize your analytical workflow?
              </h2>

              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                Whether you need in-line Process Analytical Technology (PAT), raw material identification, or advanced powder and tablet testing, our application specialists in Ireland and the UK are here to advise you.
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                  <Link to="/DiscussYourApplicationPage">
                    Discuss Your Application
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-border bg-background hover:bg-accent text-foreground">
                  <Link to="/BookADemoPage">
                    Book a Demonstration
                  </Link>
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-6 pt-2 text-xs sm:text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Clock className="size-4 text-primary" />
                  <span>Prompt technical response</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-4 text-primary" />
                  <span>Direct specialist consultation</span>
                </div>
              </div>
            </motion.div>

            {/* Right Column: Key Benefits Cards */}
            <motion.div variants={itemVariants} className="lg:col-span-5 flex flex-col gap-4">
              <div className="space-y-3.5">
                {keyBenefits.map((benefit, i) => <Card key={benefit.title} data-index={i} className="bg-card text-card-foreground border-border/80 shadow-xs hover:shadow-md transition-shadow py-4">
                    <CardContent className="px-5 flex items-start gap-3.5">
                      <div className="rounded-md bg-secondary text-primary p-2 shrink-0 mt-0.5 border border-border">
                        <CheckCircle2 className="size-4" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="font-default font-semibold text-sm sm:text-base text-foreground">
                          {benefit.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                          {benefit.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>)}
              </div>

              {/* Assistance Callout */}
              <div className="bg-background/80 rounded-xl p-4 border border-border flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <MessageSquare className="size-4 text-primary" />
                  </div>
                  <div className="text-xs">
                    <p className="font-semibold text-foreground">Need urgent technical support?</p>
                    <p className="text-muted-foreground">Submit a support request directly.</p>
                  </div>
                </div>
                <Button asChild variant="ghost" size="sm" className="shrink-0 text-primary hover:text-primary hover:bg-primary/10">
                  <Link to="/SupportRequestPage">
                    Get Support
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}